import { graph, rest } from "@/store/api";
import { RootState } from "@/store/types";
import { ActionTree } from "vuex/types";
import { FileUploadState } from "./types";
import { gql } from "@apollo/client/core";
import _ from "lodash";
import * as Sentry from "@sentry/browser";

/**
 * Actions for the File Storage module.
 * Handles async operations for file uploads and storage backend queries.
 */
const actions: ActionTree<FileUploadState, RootState> = {
  /**
   * Uploads an image to Ceph storage for MiniApp.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {FormData} file - File data to upload
   * @returns {Promise<Object>} Upload response with file URL
   */
  MINI_APP_UPLOAD_IMAGE: async ({ state, commit }, file) => {
    return rest("post", "miniapp_ceph_images_upload", file)
      .then((response) => {
        return response;
      })
      .catch((err) => {
        // beforeSend in sentry.ts will drop 4xx; we still capture here for 5xx/network.
        // (The shared rest axios interceptor also now skips logging 4xx.)
        Sentry.captureException(err);
        throw err;
      });
  },

  /**
   * Uploads a document to Ceph storage for MiniApp.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {FormData} file - File data to upload
   * @returns {Promise<Object>} Upload response with file URL
   */
  MINI_APP_UPLOAD_DOCUMENT: async ({ state, commit }, file) => {
    return rest("post", "miniapp_ceph_documents_upload", file)
      .then((response) => {
        return response;
      })
      .catch((err) => {
        // beforeSend in sentry.ts will drop 4xx; we still capture here for 5xx/network.
        // (The shared rest axios interceptor also now skips logging 4xx.)
        Sentry.captureException(err);
        throw err;
      });
  },

  /**
   * Fetches the configured storage backend type from the server.
   * Commits the result to state and defaults to 'azure' if not set.
   *
   * @param {ActionContext} context - Vuex action context
   * @returns {Promise<string>} Storage type ('azure' or 'ceph')
   */
  GET_STORAGE_TYPE: async ({ commit }) => {
    return graph
      .query({
        query: gql`
          query {
            Server {
              storageType
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((response) => {
        const storageType = _.get(response, "data.Server.storageType", "azure");
        commit("SET_STORAGE_TYPE", storageType);
        return storageType;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Retrieves all images from the configured storage backend.
   * Calls different endpoints based on storage type (Azure or Ceph).
   *
   * @param {ActionContext} context - Vuex action context
   * @returns {Promise<Object>} Image list from storage backend
   */
  GET_ALL_IMAGES: async ({ state, commit, rootState }) => {
    let response;
    try {
      if (state.storageType === "azure") {
        response = await rest("get", `getAllFolders?brain=${rootState.brain}`, {});
      } else if (state.storageType === "ceph") {
        response = await rest("get", "miniapp_ceph_get_images", {});
      }
    } catch (err) {
      // beforeSend drops 4xx client errors now.
      Sentry.captureException(err);
      throw err;
    }
    return response;
  },
};

export default actions;
